/* =====================================================================
   LUMIQO — lq-theme.js (ES module, loaded once from layout/theme.liquid)
   Shared, data-attribute driven behaviour for every lq- section.
   Re-initialises on theme editor events; ported from concepts/08-final/theme.js.

   MODULES
   1  Reveal on scroll ... .lq-reveal (siblings auto-stagger; html.lq-anim gates it)
   2  Carousel ........... [data-lq-carousel="track|fade"]
                           data-lq-autoplay="6000" · data-lq-pause-on-hover · data-lq-start="0"
                           children [data-lq-carousel-viewport|track|slide|prev|next|dots|toggle]
   3  Accordion .......... [data-lq-accordion] (+ data-lq-accordion-single) > [data-lq-accordion-trigger]
   4  Tabs ............... [data-lq-tabs] > [data-lq-tab][aria-controls] + [data-lq-tab-panel]
   5  Add to cart ........ [data-lq-add-to-cart] data-variant-id + data-quantity | data-items='[{"id":1,"quantity":1}]'
                           + form[data-lq-autosubmit]: submits when one of its selects changes
   6  Editor events ...... shopify:section:load|unload|select|deselect, shopify:block:select|deselect

   Public API: window.Lumiqo = { init(root), destroy(root), carousels, money(cents), reducedMotion(), t(key, vars), isRtl(el) }
   Fires `lq:ready` on document once initialised.

   i18n: UI strings come from <script type="application/json" id="lq-strings"> (snippets/lq-tokens.liquid,
   locales "lq.*"); per-element overrides via data attributes. No storefront text is hard-coded here.
   RTL: direction is read with getComputedStyle(el).direction; track carousels, arrow keys and swipe
   invert in RTL (scrollLeft is negative there).
   ===================================================================== */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const docEl = document.documentElement;
docEl.classList.add('lq-ready');

const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {Record<string, string>} */
const STRINGS = (() => {
  try {
    return JSON.parse(document.getElementById('lq-strings')?.textContent || '{}');
  } catch {
    return {};
  }
})();

/**
 * Translated UI string from lq-strings; fills [name] placeholders from vars.
 * @param {string} key
 * @param {Record<string, string | number>} [vars]
 */
const t = (key, vars = {}) => (STRINGS[key] || '').replace(/\[(\w+)\]/g, (_, name) => String(vars[name] ?? ''));

/** @param {Element} el */
const isRtl = (el) => getComputedStyle(el).direction === 'rtl';
const reducedMotion = () => motionQuery.matches;
const animationsOn = () => docEl.classList.contains('lq-anim') && !reducedMotion();

/**
 * @param {string} selector
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
const $$ = (selector, root = document) => {
  const list = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll(selector)));
  if (root instanceof HTMLElement && root.matches(selector)) list.unshift(root);
  return list;
};

/** @param {HTMLElement} el */
const isVisible = (el) => !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));

/**
 * Formats cents with the shop's money format (falls back to $0.00).
 * @param {number} cents
 */
function money(cents) {
  const format = window.Shopify?.money_format || window.Theme?.moneyFormat || '${{amount}}';
  const value = (Number(cents) || 0) / 100;
  const fixed = (n, d, t = ',', dec = '.') => {
    const [i, f] = n.toFixed(d).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? dec + f : '');
  };
  return String(format).replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return fixed(value, 0);
      case 'amount_with_comma_separator': return fixed(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fixed(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fixed(value, 2, "'");
      default: return fixed(value, 2);
    }
  });
}

/* ---------------------------------------------------------------
   1  REVEAL ON SCROLL
   --------------------------------------------------------------- */
/** @type {IntersectionObserver | null} */
let revealObserver = null;

/** @param {ParentNode} root */
function initReveal(root) {
  const els = $$('.lq-reveal', root).filter((el) => !el.classList.contains('is-revealed'));
  if (!els.length) return;
  for (const el of els) {
    const parent = el.parentElement;
    if (!parent) continue;
    const siblings = Array.from(parent.children).filter((c) => c.classList.contains('lq-reveal'));
    const i = siblings.indexOf(el);
    if (i > 0) el.style.setProperty('--lq-reveal-delay', `${Math.min(i, 6) * 0.08}s`);
  }
  if (!animationsOn() || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
    for (const el of els) el.classList.add('is-revealed');
    return;
  }
  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        revealObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
  );
  for (const el of els) revealObserver.observe(el);
}

/* ---------------------------------------------------------------
   2  CAROUSEL
   --------------------------------------------------------------- */
/** @type {Map<HTMLElement, LqCarousel>} */
const carousels = new Map();

class LqCarousel {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.mode = root.dataset.lqCarousel === 'fade' ? 'fade' : 'track';
    this.abort = new AbortController();
    this.delay = parseInt(root.dataset.lqAutoplay || '0', 10) || 0;
    this.index = 0;
    /** @type {number[]} */
    this.positions = [0];
    /** @type {number | undefined} */
    this.timer = undefined;
    this.userPaused = false;
    this.holdPaused = false;
    this.editorPaused = false;
    this.forcePlay = false;
    /** @type {HTMLButtonElement[]} */
    this.dots = [];
    /** @type {ResizeObserver | null} */
    this.resizeObserver = null;
    /** @type {number | null} */
    this.raf = null;

    root.classList.add('lq-carousel', `lq-carousel--${this.mode}`);
    this.viewport = this.own('[data-lq-carousel-viewport]')[0];
    this.slides = this.own('[data-lq-carousel-slide]');
    this.prevBtns = this.own('[data-lq-carousel-prev]');
    this.nextBtns = this.own('[data-lq-carousel-next]');
    this.dotsWrap = this.own('[data-lq-carousel-dots]')[0];
    this.toggle = this.own('[data-lq-carousel-toggle]')[0];
    if (!this.viewport || !this.slides.length) return;
    this.setup();
  }

  /** Elements belonging to this carousel (not to a nested one). @param {string} sel */
  own(sel) {
    return $$(sel, this.root).filter((el) => el.closest('[data-lq-carousel]') === this.root);
  }

  setup() {
    const { root, viewport, slides, abort } = this;
    const signal = abort.signal;
    if (this.delay && root.style.getPropertyValue('--lq-autoplay') === '') {
      root.style.setProperty('--lq-autoplay', `${this.delay / 1000}s`);
    }
    if (!root.hasAttribute('role')) root.setAttribute('role', 'region');
    if (!root.hasAttribute('aria-label') && !root.hasAttribute('aria-labelledby')) root.setAttribute('aria-label', t('carousel'));
    root.setAttribute('aria-roledescription', 'carousel');
    slides.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      if (!slide.hasAttribute('aria-label')) slide.setAttribute('aria-label', t('slideStatus', { index: i + 1, length: slides.length }));
    });

    for (const b of this.prevBtns) b.addEventListener('click', () => this.prev(true), { signal });
    for (const b of this.nextBtns) b.addEventListener('click', () => this.next(true), { signal });
    root.addEventListener(
      'keydown',
      (e) => {
        if (e.target instanceof HTMLElement && e.target.matches('input, textarea, select')) return;
        const rtl = isRtl(root);
        if (e.key === 'ArrowRight') { e.preventDefault(); rtl ? this.prev(true) : this.next(true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); rtl ? this.next(true) : this.prev(true); }
      },
      { signal }
    );

    if (this.toggle) {
      if (!this.delay) this.toggle.hidden = true;
      this.toggle.addEventListener(
        'click',
        () => {
          if (this.playing()) this.userPaused = true;
          else { this.userPaused = false; this.forcePlay = true; this.holdPaused = false; }
          this.restart();
        },
        { signal }
      );
    }

    // pause while pointer / keyboard focus is on the slides themselves (not the controls)
    const holdOn = () => { this.holdPaused = true; root.classList.remove('is-playing'); };
    const holdOff = () => { this.holdPaused = false; this.restart(); };
    if (root.hasAttribute('data-lq-pause-on-hover')) {
      viewport.addEventListener('mouseenter', holdOn, { signal });
      viewport.addEventListener('mouseleave', holdOff, { signal });
    }
    viewport.addEventListener('focusin', holdOn, { signal });
    viewport.addEventListener(
      'focusout',
      (e) => { if (!(e.relatedTarget instanceof Node) || !viewport.contains(e.relatedTarget)) holdOff(); },
      { signal }
    );

    if (this.mode === 'fade') {
      let sx = /** @type {number | null} */ (null);
      let sy = 0;
      viewport.style.touchAction = 'pan-y';
      viewport.addEventListener(
        'pointerdown',
        (e) => { if (e.pointerType === 'mouse' && e.button !== 0) return; sx = e.clientX; sy = e.clientY; },
        { passive: true, signal }
      );
      viewport.addEventListener(
        'pointerup',
        (e) => {
          if (sx === null) return;
          const dx = e.clientX - sx;
          const dy = e.clientY - sy;
          sx = null;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            // swiping toward the inline start advances (left in LTR, right in RTL)
            const forward = isRtl(root) ? dx > 0 : dx < 0;
            forward ? this.next(true) : this.prev(true);
          }
        },
        { signal }
      );
      viewport.addEventListener('pointercancel', () => { sx = null; }, { signal });
      const start = parseInt(root.dataset.lqStart || '', 10);
      const preset = slides.findIndex((s) => s.classList.contains('is-active'));
      this.index = Number.isNaN(start) ? Math.max(preset, 0) : Math.min(Math.max(start, 0), slides.length - 1);
      this.buildDots(slides.length);
      this.renderFade();
      this.updateDots();
      root.classList.toggle('is-static', slides.length <= 1);
    } else {
      viewport.addEventListener(
        'scroll',
        () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => { this.raf = null; this.syncFromScroll(); });
        },
        { passive: true, signal }
      );
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(viewport);
      } else {
        window.addEventListener('resize', () => this.measure(), { signal });
      }
      this.measure();
    }

    motionQuery.addEventListener('change', () => this.restart(), { signal });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.restart(); }, { signal });
    this.syncScheme();
    this.restart();
  }

  /** @param {number} count */
  buildDots(count) {
    if (!this.dotsWrap) return;
    this.dotsWrap.replaceChildren();
    this.dots = [];
    for (let i = 0; i < count; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lq-carousel__dot';
      const name = this.mode === 'fade' ? this.slides[i]?.dataset.slideName : '';
      const label = t('goToSlide', { index: i + 1 });
      b.setAttribute('aria-label', name ? `${label}: ${name}` : label);
      b.addEventListener('click', () => this.goTo(i, true));
      this.dotsWrap.appendChild(b);
      this.dots.push(b);
    }
  }

  updateDots() {
    this.dots.forEach((d, i) => (i === this.index ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
  }

  renderFade() {
    this.slides.forEach((s, i) => {
      const on = i === this.index;
      s.classList.toggle('is-active', on);
      s.setAttribute('aria-hidden', on ? 'false' : 'true');
      s.inert = !on;
    });
  }

  /**
   * Distance of a slide from the first slide along the inline axis (always positive, LTR or RTL).
   * @param {HTMLElement} slide
   */
  offsetOf(slide) {
    const first = this.slides[0];
    if (!first) return 0;
    if (isRtl(this.root)) return first.offsetLeft + first.offsetWidth - (slide.offsetLeft + slide.offsetWidth);
    return slide.offsetLeft - first.offsetLeft;
  }

  measure() {
    const { viewport, slides } = this;
    const max = viewport.scrollWidth - viewport.clientWidth;
    if (!slides[0]) return;
    this.positions = [];
    for (const s of slides) {
      const p = this.offsetOf(s);
      if (p < max - 4) this.positions.push(p);
    }
    if (!this.positions.length || max > 4) this.positions.push(Math.max(0, max));
    if (max <= 4) this.positions = [0];
    this.root.classList.toggle('is-static', this.positions.length <= 1);
    if (this.dots.length !== this.positions.length) this.buildDots(this.positions.length);
    this.syncFromScroll();
  }

  syncFromScroll() {
    const x = Math.abs(this.viewport.scrollLeft);
    let best = 0;
    let dist = Infinity;
    this.positions.forEach((p, i) => {
      const d = Math.abs(p - x);
      if (d < dist) { dist = d; best = i; }
    });
    this.index = best;
    this.updateDots();
    this.updateArrows();
  }

  updateArrows() {
    if (this.mode === 'fade') return;
    const atStart = this.index <= 0;
    const atEnd = this.index >= this.positions.length - 1;
    for (const b of this.prevBtns) if (b instanceof HTMLButtonElement) b.disabled = atStart && !this.delay;
    for (const b of this.nextBtns) if (b instanceof HTMLButtonElement) b.disabled = atEnd && !this.delay;
  }

  count() {
    return this.mode === 'fade' ? this.slides.length : this.positions.length;
  }

  /**
   * @param {number} i
   * @param {boolean} [fromUser]
   * @param {boolean} [instant]
   */
  goTo(i, fromUser = false, instant = false) {
    const n = this.count();
    if (!n) return;
    this.index = ((i % n) + n) % n;
    if (this.mode === 'fade') {
      this.renderFade();
      this.updateDots();
    } else {
      // RTL scroll positions run from 0 to negative values
      const left = (this.positions[this.index] || 0) * (isRtl(this.root) ? -1 : 1);
      this.viewport.scrollTo({ left, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
      this.updateDots();
      this.updateArrows();
    }
    this.syncScheme();
    if (fromUser) this.restart();
    this.root.dispatchEvent(new CustomEvent('lq:carousel:change', { bubbles: true, detail: { index: this.index } }));
  }

  /** Scroll/fade to the slide element itself (track mode maps slide → stop). @param {HTMLElement} slide */
  goToSlide(slide, instant = false) {
    const i = this.slides.indexOf(slide);
    if (i < 0) return;
    if (this.mode === 'fade') return this.goTo(i, false, instant);
    const target = this.offsetOf(slide);
    let best = 0;
    this.positions.forEach((p, idx) => { if (p <= target + 4) best = idx; });
    this.goTo(best, false, instant);
  }

  /** Mirrors the active slide's data-lq-slide-scheme on the root as data-active-scheme. */
  syncScheme() {
    const active = this.mode === 'fade' ? this.slides[this.index] : null;
    const scheme = active?.dataset.lqSlideScheme;
    if (scheme) this.root.dataset.activeScheme = scheme;
  }

  /** @param {boolean} [fromUser] */
  next(fromUser) { this.goTo(this.index + 1, fromUser); }

  /** @param {boolean} [fromUser] */
  prev(fromUser) { this.goTo(this.index - 1, fromUser); }

  playing() {
    return !!this.delay && !this.userPaused && !this.editorPaused && (this.forcePlay || !reducedMotion());
  }

  tick = () => {
    if (this.holdPaused || document.hidden || !isVisible(this.root)) return;
    this.next(false);
  };

  restart() {
    window.clearInterval(this.timer);
    this.timer = undefined;
    const playing = this.playing();
    if (playing) this.timer = window.setInterval(this.tick, this.delay);
    // restart the dot progress animation
    this.root.classList.remove('is-playing');
    if (playing && !this.holdPaused) {
      void this.root.offsetWidth;
      this.root.classList.add('is-playing');
    }
    this.viewport.setAttribute('aria-live', playing ? 'off' : 'polite');
    if (this.toggle) {
      const paused = !playing;
      this.toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      const label = paused ? this.toggle.dataset.labelPlay || t('slideshowPlay') : this.toggle.dataset.labelPause || t('slideshowPause');
      this.toggle.setAttribute('aria-label', label);
    }
  }

  /** @param {boolean} paused */
  setEditorPaused(paused) {
    this.editorPaused = paused;
    this.restart();
  }

  refresh() {
    if (this.mode === 'track') this.measure();
  }

  destroy() {
    window.clearInterval(this.timer);
    this.abort.abort();
    this.resizeObserver?.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
    carousels.delete(this.root);
  }
}

/** @param {ParentNode} root */
function initCarousels(root) {
  for (const el of $$('[data-lq-carousel]', root)) {
    if (carousels.has(el)) continue;
    const c = new LqCarousel(el);
    if (c.viewport && c.slides.length) carousels.set(el, c);
  }
}

/* ---------------------------------------------------------------
   3  ACCORDION
   --------------------------------------------------------------- */
/** @type {WeakMap<HTMLElement, AbortController>} */
const accordions = new WeakMap();

/**
 * @param {HTMLElement} trigger
 * @param {boolean} open
 */
function setAccordion(trigger, open) {
  const panelId = trigger.getAttribute('aria-controls');
  const panel = panelId ? document.getElementById(panelId) : null;
  trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (!panel) return;
  const wasHidden = panel.hidden;
  panel.hidden = !open;
  if (open && wasHidden && !reducedMotion()) {
    panel.classList.remove('is-opening');
    void panel.offsetWidth;
    panel.classList.add('is-opening');
  }
}

/** @param {ParentNode} root */
function initAccordions(root) {
  for (const acc of $$('[data-lq-accordion]', root)) {
    if (accordions.has(acc)) continue;
    const abort = new AbortController();
    accordions.set(acc, abort);
    const single = acc.hasAttribute('data-lq-accordion-single');
    const triggers = $$('[data-lq-accordion-trigger]', acc).filter((t) => t.closest('[data-lq-accordion]') === acc);
    triggers.forEach((t, i) => {
      setAccordion(t, t.getAttribute('aria-expanded') === 'true');
      t.addEventListener(
        'click',
        () => {
          const willOpen = t.getAttribute('aria-expanded') !== 'true';
          if (willOpen && single) for (const o of triggers) if (o !== t) setAccordion(o, false);
          setAccordion(t, willOpen);
        },
        { signal: abort.signal }
      );
      t.addEventListener(
        'keydown',
        (e) => {
          let n = null;
          if (e.key === 'ArrowDown') n = triggers[(i + 1) % triggers.length];
          if (e.key === 'ArrowUp') n = triggers[(i - 1 + triggers.length) % triggers.length];
          if (e.key === 'Home') n = triggers[0];
          if (e.key === 'End') n = triggers[triggers.length - 1];
          if (n) { e.preventDefault(); n.focus(); }
        },
        { signal: abort.signal }
      );
    });
  }
}

/* ---------------------------------------------------------------
   4  TABS
   --------------------------------------------------------------- */
/** @type {WeakMap<HTMLElement, AbortController>} */
const tabsets = new WeakMap();

/**
 * @param {HTMLElement} tabset
 * @param {HTMLElement} tab
 * @param {boolean} [focus]
 */
function selectTab(tabset, tab, focus = false) {
  const tabs = $$('[data-lq-tab]', tabset).filter((t) => t.closest('[data-lq-tabs]') === tabset);
  for (const t of tabs) {
    const on = t === tab;
    t.setAttribute('aria-selected', on ? 'true' : 'false');
    t.tabIndex = on ? 0 : -1;
    const panelId = t.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) continue;
    const wasHidden = panel.hidden;
    panel.hidden = !on;
    if (on && wasHidden && !reducedMotion()) {
      panel.classList.remove('is-entering');
      void panel.offsetWidth;
      panel.classList.add('is-entering');
    }
  }
  if (focus) tab.focus();
  tabset.dispatchEvent(new CustomEvent('lq:tabs:change', { bubbles: true, detail: { tab, index: tabs.indexOf(tab) } }));
}

/** @param {ParentNode} root */
function initTabs(root) {
  for (const tabset of $$('[data-lq-tabs]', root)) {
    if (tabsets.has(tabset)) continue;
    const abort = new AbortController();
    tabsets.set(tabset, abort);
    const tabs = $$('[data-lq-tab]', tabset).filter((t) => t.closest('[data-lq-tabs]') === tabset);
    if (!tabs.length) continue;
    const initial = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
    if (initial) selectTab(tabset, initial);
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => selectTab(tabset, t), { signal: abort.signal });
      t.addEventListener(
        'keydown',
        (e) => {
          let n = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') n = tabs[0];
          if (e.key === 'End') n = tabs[tabs.length - 1];
          if (n) { e.preventDefault(); selectTab(tabset, n, true); }
        },
        { signal: abort.signal }
      );
    });
  }
}

/* ---------------------------------------------------------------
   5  ADD TO CART (Horizon cart drawer + bubble via CartAddEvent)
   --------------------------------------------------------------- */
/**
 * Reads the items to add from the button.
 * @param {HTMLElement} btn
 * @returns {{id: number, quantity: number}[]}
 */
function readItems(btn) {
  if (btn.dataset.items) {
    try {
      const parsed = JSON.parse(btn.dataset.items);
      if (Array.isArray(parsed)) {
        return parsed
          .map((it) => ({ id: Number(it.id), quantity: Math.max(1, parseInt(it.quantity, 10) || 1) }))
          .filter((it) => it.id > 0);
      }
    } catch (error) {
      console.error('[lq] invalid data-items JSON', error);
    }
    return [];
  }
  const id = Number(btn.dataset.variantId);
  if (!id) return [];
  return [{ id, quantity: Math.max(1, parseInt(btn.dataset.quantity || '1', 10) || 1) }];
}

/**
 * Finds (or creates) the inline message element for a button.
 * Use [data-lq-atc-message] inside the closest [data-lq-atc-scope] to place it yourself.
 * @param {HTMLElement} btn
 */
function messageFor(btn) {
  const scope = btn.closest('[data-lq-atc-scope]') || btn.parentElement;
  let msg = /** @type {HTMLElement | null} */ (scope?.querySelector('[data-lq-atc-message]') || null);
  if (!msg) {
    msg = document.createElement('p');
    msg.className = 'lq-atc-msg';
    msg.setAttribute('data-lq-atc-message', '');
    msg.setAttribute('role', 'status');
    msg.setAttribute('aria-live', 'polite');
    (btn.closest('form') || btn).insertAdjacentElement('afterend', msg);
  }
  return msg;
}

/** @param {HTMLElement} btn @param {boolean} busy */
function setBusy(btn, busy) {
  btn.classList.toggle('is-loading', busy);
  btn.setAttribute('aria-busy', busy ? 'true' : 'false');
  if (btn instanceof HTMLButtonElement) btn.disabled = busy;
}

/** @param {HTMLElement} btn */
async function addToCart(btn) {
  if (btn.classList.contains('is-loading')) return;
  const items = readItems(btn);
  const msg = messageFor(btn);
  if (!items.length) return;
  const routes = window.Theme?.routes;
  if (!routes?.cart_add_url) return;

  const sections = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  setBusy(btn, true);
  msg.setAttribute('role', 'status');
  msg.textContent = btn.dataset.addingText || t('adding');
  try {
    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const json = await response.json();
    if (!response.ok || json.status) {
      const text = json.description || json.message || btn.dataset.errorText || t('addError');
      document.dispatchEvent(new CartErrorEvent(btn.id || 'lq-add', json.message, json.description, json.errors));
      msg.textContent = text;
      msg.setAttribute('role', 'alert');
      return;
    }

    // Horizon's cart bubble treats itemCount as the cart TOTAL for non product-form sources,
    // so read the real total from /cart.js.
    let itemCount = items.reduce((sum, it) => sum + it.quantity, 0);
    try {
      const cartUrl = `${routes.cart_url || '/cart'}.js`;
      const cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
      if (typeof cart.item_count === 'number') itemCount = cart.item_count;
    } catch {
      /* keep added quantity */
    }

    document.dispatchEvent(
      new CartAddEvent({}, 'lq-add', {
        source: 'lq',
        itemCount,
        variantId: String(items[0]?.id ?? ''),
        sections: json.sections,
      })
    );
    btn.classList.add('is-added');
    window.setTimeout(() => btn.classList.remove('is-added'), 2000);
    msg.setAttribute('role', 'status');
    msg.textContent = btn.dataset.successText || t('added');
    window.setTimeout(() => { if (msg.textContent === (btn.dataset.successText || t('added'))) msg.textContent = ''; }, 4000);
  } catch (error) {
    console.error('[lq] add to cart failed', error);
    document.dispatchEvent(new CartErrorEvent(btn.id || 'lq-add', String(error), '', {}));
    msg.setAttribute('role', 'alert');
    msg.textContent = btn.dataset.errorText || t('addError');
  } finally {
    setBusy(btn, false);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const btn = /** @type {HTMLElement | null} */ (target?.closest('[data-lq-add-to-cart]') || null);
  if (!btn) return;
  event.preventDefault();
  addToCart(btn);
});

/* Footer language / country selects: submit on change (the form's own button is the no-JS fallback) */
document.addEventListener('change', (event) => {
  const select = event.target instanceof HTMLSelectElement ? event.target : null;
  const form = select?.closest('form[data-lq-autosubmit]');
  if (form instanceof HTMLFormElement) form.requestSubmit();
});

/* ---------------------------------------------------------------
   6  INIT, DESTROY, EDITOR EVENTS
   --------------------------------------------------------------- */
/** @param {ParentNode} [root] */
function init(root = document) {
  initCarousels(root);
  initAccordions(root);
  initTabs(root);
  initReveal(root);
}

/** @param {ParentNode} root */
function destroy(root) {
  for (const [el, c] of carousels) if (root.contains(el)) c.destroy();
  for (const el of $$('[data-lq-accordion]', root)) { accordions.get(el)?.abort(); accordions.delete(el); }
  for (const el of $$('[data-lq-tabs]', root)) { tabsets.get(el)?.abort(); tabsets.delete(el); }
  if (revealObserver) for (const el of $$('.lq-reveal', root)) revealObserver.unobserve(el);
}

/** @param {Event} event */
const eventRoot = (event) => (event.target instanceof HTMLElement ? event.target : null);

document.addEventListener('shopify:section:load', (e) => { const r = eventRoot(e); if (r) init(r); });
document.addEventListener('shopify:section:unload', (e) => { const r = eventRoot(e); if (r) destroy(r); });

document.addEventListener('shopify:section:select', (e) => {
  const r = eventRoot(e);
  if (!r) return;
  for (const [el, c] of carousels) if (r.contains(el)) c.setEditorPaused(true);
});
document.addEventListener('shopify:section:deselect', (e) => {
  const r = eventRoot(e);
  if (!r) return;
  for (const [el, c] of carousels) if (r.contains(el)) c.setEditorPaused(false);
});

document.addEventListener('shopify:block:select', (e) => {
  const block = eventRoot(e);
  if (!block) return;
  // carousel slide → bring it into view and hold
  const slide = /** @type {HTMLElement | null} */ (block.closest('[data-lq-carousel-slide]') || block.querySelector('[data-lq-carousel-slide]'));
  const carouselRoot = slide?.closest('[data-lq-carousel]');
  const carousel = carouselRoot instanceof HTMLElement ? carousels.get(carouselRoot) : undefined;
  if (slide && carousel) {
    carousel.setEditorPaused(true);
    carousel.goToSlide(slide, !!e.detail?.load);
  }
  // accordion item → open it
  const trigger = block.matches('[data-lq-accordion-trigger]')
    ? block
    : /** @type {HTMLElement | null} */ (block.querySelector('[data-lq-accordion-trigger]'));
  if (trigger) setAccordion(trigger, true);
  // tab panel / tab → select it
  const tab = block.matches('[data-lq-tab]') ? block : null;
  const panel = block.closest('[data-lq-tab-panel]');
  const tabset = (tab || panel)?.closest('[data-lq-tabs]');
  if (tabset instanceof HTMLElement) {
    const target = tab || (panel ? tabset.querySelector(`[data-lq-tab][aria-controls="${panel.id}"]`) : null);
    if (target instanceof HTMLElement) selectTab(tabset, target);
  }
  // reveal immediately so the editor never shows a hidden block
  for (const el of $$('.lq-reveal', block)) el.classList.add('is-revealed');
});

document.addEventListener('shopify:block:deselect', (e) => {
  const block = eventRoot(e);
  const carouselRoot = block?.closest('[data-lq-carousel]');
  const carousel = carouselRoot instanceof HTMLElement ? carousels.get(carouselRoot) : undefined;
  carousel?.setEditorPaused(false);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

/** Public API for section scripts (assets/lq-<name>.js). */
window.Lumiqo = {
  init,
  destroy,
  carousels,
  money,
  reducedMotion,
  addToCart,
  t,
  isRtl,
};
document.dispatchEvent(new CustomEvent('lq:ready'));
